import React, { useState, useCallback, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDropzone } from 'react-dropzone';
import Modal from '../common/Modal';
import LoadingSpinner from '../common/LoadingSpinner';
import apiClient from '../../services/apiClient';

const RFPUploadModal = ({ isOpen, onClose, onUploadComplete }) => {
  const [sourceUrl, setSourceUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedModel, setSelectedModel] = useState('');
  const [availableModels, setAvailableModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(true);

  // Load available models when modal opens
  useEffect(() => {
    if (isOpen) {
      loadAvailableModels();
    }
  }, [isOpen]);

  const loadAvailableModels = async () => {
    try {
      setModelsLoading(true);
      const response = await apiClient.getAIModels();

      if (response.success && response.data.models) {
        setAvailableModels(response.data.models);

        // Set default model (the one marked as isDefault)
        const defaultModel = response.data.models.find(m => m.isDefault);
        if (defaultModel) {
          setSelectedModel(defaultModel.model);
        }
      }
      setModelsLoading(false);
    } catch (error) {
      console.error('Failed to load AI models:', error);
      setError('Failed to load AI models. Using default.');
      setModelsLoading(false);
    }
  };

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      setSelectedFile(acceptedFiles[0]);
      setError('');
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/zip': ['.zip'],
      'text/plain': ['.txt']
    },
    multiple: false,
    maxSize: 100 * 1024 * 1024, // 100MB
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }

    if (!selectedModel) {
      setError('Please select an AI model');
      return;
    }

    setUploading(true);
    setError('');

    try {
      await onUploadComplete(selectedFile, sourceUrl || null, selectedModel);
      handleClose();
    } catch (err) {
      setError(err.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setSourceUrl('');
    setError('');
    setUploading(false);
    onClose();
  };

  const removeFile = () => {
    setSelectedFile(null);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Upload RFP Document"
      widthClassName="max-w-4xl"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Source URL Input */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            RFP Source URL (Optional)
          </label>
          <input
            type="url"
            value={sourceUrl}
            onChange={(e) => setSourceUrl(e.target.value)}
            placeholder="https://rfpmart.com/..."
            className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            disabled={uploading}
          />
          <p className="mt-1 text-sm text-slate-500">
            Enter the URL where this RFP was found for future reference
          </p>
        </div>

        {/* AI Model Selection */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            AI Model *
          </label>
          {modelsLoading ? (
            <div className="w-full px-4 py-2 border border-slate-300 rounded-lg bg-slate-50 text-slate-500">
              Loading models...
            </div>
          ) : (
            <select
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              disabled={uploading}
              required
            >
              <option value="">Select AI Model</option>

              {/* OpenAI Models */}
              {availableModels.filter(m => m.provider === 'openai').length > 0 && (
                <optgroup label="OpenAI">
                  {availableModels
                    .filter(m => m.provider === 'openai')
                    .map((model) => (
                      <option key={model.model} value={model.model}>
                        {model.name} {model.isDefault && '(Recommended)'}
                      </option>
                    ))}
                </optgroup>
              )}

              {/* Anthropic Models */}
              {availableModels.filter(m => m.provider === 'anthropic').length > 0 && (
                <optgroup label="Anthropic">
                  {availableModels
                    .filter(m => m.provider === 'anthropic')
                    .map((model) => (
                      <option key={model.model} value={model.model}>
                        {model.name} {model.isDefault && '(Recommended)'}
                      </option>
                    ))}
                </optgroup>
              )}

              {/* Google Models */}
              {availableModels.filter(m => m.provider === 'google').length > 0 && (
                <optgroup label="Google">
                  {availableModels
                    .filter(m => m.provider === 'google')
                    .map((model) => (
                      <option key={model.model} value={model.model}>
                        {model.name} {model.isDefault && '(Recommended)'}
                      </option>
                    ))}
                </optgroup>
              )}
            </select>
          )}
          <p className="mt-1 text-sm text-slate-500">
            Choose which AI model to use for analyzing this RFP
          </p>
        </div>

        {/* File Upload Dropzone */}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">
            RFP File *
          </label>

          {!selectedFile ? (
            <div
              {...getRootProps()}
              className={`
                border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
                ${isDragActive
                  ? 'border-primary-500 bg-primary-50'
                  : 'border-slate-300 hover:border-primary-400 hover:bg-slate-50'
                }
              `}
            >
              <input {...getInputProps()} />
              <FontAwesomeIcon
                icon="cloud-upload-alt"
                className={`text-4xl mb-4 ${isDragActive ? 'text-primary-500' : 'text-slate-400'}`}
              />
              <p className="text-slate-700 font-medium mb-2">
                {isDragActive
                  ? 'Drop the file here...'
                  : 'Drag & drop RFP file here, or click to browse'}
              </p>
              <p className="text-sm text-slate-500">
                Supports: PDF, Word (.doc, .docx), ZIP archives, or Text files
              </p>
              <p className="text-xs text-slate-400 mt-2">
                Maximum file size: 100MB
              </p>
            </div>
          ) : (
            <div className="border-2 border-green-200 bg-green-50 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <FontAwesomeIcon
                    icon={selectedFile.name.endsWith('.zip') ? 'file-archive' : 'file-pdf'}
                    className="text-2xl text-green-600"
                  />
                  <div>
                    <p className="font-medium text-slate-900">{selectedFile.name}</p>
                    <p className="text-sm text-slate-600">
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                      {selectedFile.name.endsWith('.zip') && (
                        <span className="ml-2 text-amber-600">
                          • ZIP file - documents will be extracted
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={removeFile}
                  disabled={uploading}
                  className="text-red-500 hover:text-red-700 p-2 transition-colors disabled:opacity-50"
                >
                  <FontAwesomeIcon icon="times" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            <FontAwesomeIcon icon="exclamation-triangle" className="mr-2" />
            {error}
          </div>
        )}

        {/* Information Box */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <FontAwesomeIcon icon="info-circle" className="text-blue-500 mt-1" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">What happens next:</p>
              <ul className="list-disc list-inside space-y-1 text-blue-700">
                <li>Your file will be uploaded and processed</li>
                <li>If it's a ZIP file, you'll select which documents to analyze</li>
                <li>You'll choose company context documents for comparison</li>
                <li>AI will analyze the RFP and provide a bid/no-bid recommendation</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <button
            type="button"
            onClick={handleClose}
            disabled={uploading}
            className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!selectedFile || !selectedModel || uploading}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
          >
            {uploading ? (
              <>
                <LoadingSpinner size="sm" />
                <span>Uploading...</span>
              </>
            ) : (
              <>
                <FontAwesomeIcon icon="upload" />
                <span>Upload & Continue</span>
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default RFPUploadModal;
